import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { compareByShootingStart, earliestByShootingStart, getMaxDateTimeForOrder, getMinDateTimeForOrder, getMinDateTimeForOrderWithPrestigeProduct, roundUpToNearestTimeSegment } from 'utils/time'
import { FeatureFlag, useFeatureFlag } from 'utils/featureFlags'
import moment, { Moment } from 'moment-timezone'
import { useCallback, useMemo, useState } from 'react'
import { useGetOrder, useRescheduleOrder } from 'dataQueries'

import { AssignmentDTOClient } from 'models/assignment'
import { AxiosError } from 'axios'
import { DealDTO } from 'models/deal'
import { DealState } from 'constants/deal'
import { Nullable } from 'models/helpers'
import { PrestigeProductKinds } from 'constants/product'
import constate from 'constate'
import { liveAssignments } from 'utils/stages'
import { useAuth0 } from 'utils/auth'
import { useDynamicNow } from 'utils/hooks'
import { useTimezone } from 'components/contexts/timezone.context'

export const freeRescheduleTimeLimit = process.env.REACT_APP_FREE_RESCHEDULE_HOUR_LIMIT ?? 24

export enum RescheduleReason {
  REQUESTED_BY_CT = 'REQUESTED_BY_CT',
  WEATHER = 'WEATHER',
  ILLNESS = 'ILLNESS',
  OTHER = 'OTHER',
}

export enum RescheduleStep {
  FORM = 'FORM',
  CONFIRM = 'CONFIRM',
}

const _getLiveMissions = (order: Nullable<DealDTO>, ignoreCancelledLines: boolean): AssignmentDTOClient[] =>
  liveAssignments<AssignmentDTOClient>(order?.missions, ignoreCancelledLines)

const _getOrderStartFromAssignments = (order: Nullable<DealDTO>, ignoreCancelledLines: boolean): Moment | null => {
  if (!order?.missions) return null

  const start = earliestByShootingStart(_getLiveMissions(order, ignoreCancelledLines))?.shootingStartDateTime

  return !!start ? moment(start) : null
}

export const canOrderBeRescheduled = (order: Nullable<DealDTO>, ignoreCancelledLines = false): boolean => {
  if (!order || order.state !== DealState.NEW) return false

  const shootingStart = _getOrderStartFromAssignments(order, ignoreCancelledLines)
  if (!shootingStart) return false

  if (moment().isSameOrAfter(shootingStart)) return false

  return true
}

export const [RescheduleOrderModuleProvider, useRescheduleOrderModule] = constate(({ orderId, objectReferenceId }: { orderId: number | undefined, objectReferenceId?: string | null }) => {

  const orderQuery = useGetOrder(orderId ?? 0, !!orderId, true)
  const { roles } = useAuth0()
  const { userTimezone } = useTimezone()
  const now = useDynamicNow(userTimezone)
  const ignoreCancelledLines = useFeatureFlag(FeatureFlag.ENABLE_SF_LAST_LINE_CANCEL)

  const rescheduleMutation = useRescheduleOrder()
  const [rescheduleReason, setRescheduleReason] = useState<RescheduleReason>()
  const [rescheduleReasonComment, setRescheduleReasonComment] = useState<string>('')
  const [newDate, setNewDate] = useState<Moment | null>(null)
  const [creativeAgreed, setCreativeAgreed] = useState<boolean>()
  const [step, setStep] = useState<RescheduleStep>(RescheduleStep.FORM)

  const orderData = useMemo(() => orderQuery.data?.data, [orderQuery.data?.data])
  /** Missions by shooting start, undated last, so the assigned CT is the earliest shoot's (copied, never sorting the query cache) */
  const assignments = useMemo(() => [..._getLiveMissions(orderData, ignoreCancelledLines)].sort(compareByShootingStart), [ignoreCancelledLines, orderData])
  const assignedCT = useMemo(() => assignments.find((assignment) => !!assignment.creative)?.creative ?? undefined, [assignments])

  const isMin48Hours = useMemo(() => !assignedCT || !creativeAgreed, [assignedCT, creativeAgreed])

  const isPrestigeProductSelected = useMemo(
    () => !!orderData?.financials.products.some((product) => !!product.kind && PrestigeProductKinds.has(product.kind)),
    [orderData]
  )

  const minOrderDateTime = useMemo(() => {
    const minDate = isPrestigeProductSelected
      ? getMinDateTimeForOrderWithPrestigeProduct(now, userTimezone)
      : getMinDateTimeForOrder(now, userTimezone)
    return moment(roundUpToNearestTimeSegment(minDate, 'quarter-hour'))
  }, [isPrestigeProductSelected, now, userTimezone])

  /** The last day an order may be rescheduled to (QA-361) */
  const maxOrderDateTime = useMemo(() => moment(getMaxDateTimeForOrder(now, userTimezone)), [now, userTimezone])

  const shootingStart = useMemo(() => _getOrderStartFromAssignments(orderData, ignoreCancelledLines), [ignoreCancelledLines, orderData])

  const freeRescheduleLimit = useMemo(
    () => moment(shootingStart).subtract(freeRescheduleTimeLimit, 'hours'),
    [shootingStart]
  )

  const isBeforeLimit = useMemo(() => freeRescheduleLimit.isAfter(now), [freeRescheduleLimit, now])

  const hoursBeforeEnd = useMemo(() => moment(shootingStart).diff(now, 'hours'), [now, shootingStart])

  const isPenaltyRequired = useMemo(() => !isBeforeLimit && !!assignedCT, [assignedCT, isBeforeLimit])

  const isReschedulePossible = useMemo(
    () => !!orderData && canOrderBeRescheduled(orderData, ignoreCancelledLines),
    // Extra "now" to keep it up to date with passing time
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ignoreCancelledLines, now, orderData]
  )

  const visualProgress = useMemo(() => {
    if (!orderData) return 0
    const start = !!orderData.createdAt ? moment(orderData.createdAt) : moment().subtract(1, 'week')
    const end = shootingStart?.clone()

    if (!end) return 10

    if (now.isBefore(freeRescheduleLimit)) {
      return (now.valueOf() - start.valueOf()) / (freeRescheduleLimit.valueOf() - start.valueOf()) * 75
    }

    return Math.min((now.valueOf() - freeRescheduleLimit.valueOf()) / (end.valueOf() - freeRescheduleLimit.valueOf()) * 25 + 75, 100)

  }, [freeRescheduleLimit, now, orderData, shootingStart])

  const dateError = useMemo(() => {
    if (!newDate) return 'required'
    if (newDate.isSame(shootingStart)) return 'sameDate'
    if (isMin48Hours && newDate.isBefore(minOrderDateTime)) return 'minDate'
    // Compared by calendar day, the same granularity the picker's maxDate uses
    if (newDate.isAfter(maxOrderDateTime, 'day')) return 'maxDate'
    return undefined
  }, [newDate, shootingStart, isMin48Hours, minOrderDateTime, maxOrderDateTime])

  const isCommentError = useMemo(() => rescheduleReason === RescheduleReason.OTHER && rescheduleReasonComment === '', [rescheduleReason, rescheduleReasonComment])

  const isValid = useMemo(() => {
    if (!!dateError || isCommentError || !rescheduleReason) return false
    if (!!assignedCT && creativeAgreed === undefined) return false

    return true
  }, [assignedCT, creativeAgreed, dateError, isCommentError, rescheduleReason])

  const reset = useCallback(() => {
    setRescheduleReason(undefined)
    setRescheduleReasonComment('')
    setNewDate(null)
    setCreativeAgreed(undefined)
    setStep(RescheduleStep.FORM)
  }, [])

  const reschedule = useCallback((onSuccess?: () => void) => {
    if (!isValid) return
    if (!orderId || !newDate || !rescheduleReason) return

    // The funnel counts the client's reschedules; an admin's goes through the same dialog module
    const tracksFunnel = !roles.isAdmin
    // Taken at submit, with the clock the dialog rendered the penalty from; the comment is free text and never sent
    const eventProps = {
      orderId,
      ...(objectReferenceId ? { objectReferenceId } : {}),
      hours_to_shoot: shootingStart ? hoursBeforeEnd : null,
      penalty_required: shootingStart ? isPenaltyRequired : null,
      new_date_lead_time_hours: newDate.diff(now, 'hours'),
      photographer_agreed: creativeAgreed ?? null,
      reschedule_reason: rescheduleReason,
    }

    rescheduleMutation.mutate(
      {
        orderId,
        hasCtAgreed: creativeAgreed ?? false,
        newDateTime: newDate.toISOString(true),
        rescheduleComment: rescheduleReasonComment,
        rescheduleReason,
      },
      {
        onSuccess: () => {
          if (tracksFunnel) logAnalyticsEvent(AnalyticsEvent.RESCHEDULE_SUCCEEDED, eventProps)
          onSuccess?.()
        },
        onError: (error) => {
          if (tracksFunnel) logAnalyticsEvent(AnalyticsEvent.RESCHEDULE_FAILED, { ...eventProps, http_status: (error as AxiosError | undefined)?.response?.status ?? null })
        },
      }
    )
  }, [creativeAgreed, hoursBeforeEnd, isPenaltyRequired, isValid, newDate, now, objectReferenceId, orderId, rescheduleMutation, rescheduleReason, rescheduleReasonComment, roles.isAdmin, shootingStart])

  return {
    visualProgress,
    isBeforeLimit,
    freeRescheduleLimit,
    hoursBeforeEnd,
    assignedCT,
    orderData,
    rescheduleMutation,
    step,
    isMin48Hours,
    minOrderDateTime,
    maxOrderDateTime,
    rescheduleReason,
    rescheduleReasonComment,
    creativeAgreed,
    newDate,
    dateError,
    isCommentError,
    isValid,
    isPenaltyRequired,
    isReschedulePossible,
    setCreativeAgreed,
    setNewDate,
    setRescheduleReason,
    setRescheduleReasonComment,
    setStep,
    reset,
    reschedule,
  }
})
